import React from 'react';
import { useEffect, useRef } from 'react';
import { QuestionnaireData, Industry } from '../../types';
import { PlusCircle } from 'lucide-react';

interface IndustryStepProps {
  data: Partial<QuestionnaireData>;
  onChange: (data: Partial<QuestionnaireData>) => void;
}

const industries = [
  { id: 'technology' as Industry, label: 'Technology' },
  { id: 'retail' as Industry, label: 'Retail/E-commerce' },
  { id: 'healthcare' as Industry, label: 'Healthcare' },
  { id: 'education' as Industry, label: 'Education' },
  { id: 'finance' as Industry, label: 'Finance' },
  { id: 'creative' as Industry, label: 'Creative Services' },
  { id: 'marketing' as Industry, label: 'Marketing and Advertising' },
  { id: 'consulting' as Industry, label: 'Consulting' },
  { id: 'trades' as Industry, label: 'Trades & Construction' },
  { id: 'home-services' as Industry, label: 'Home Services' },
];

// Named export
export function IndustryStep({ data, onChange }: IndustryStepProps) {
  const [customIndustry, setCustomIndustry] = React.useState('');
  const customInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data.industry && customInputRef.current) {
      setTimeout(() => {
        customInputRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [data.industry]);

  const handleCustomIndustry = () => {
    if (customIndustry.trim()) {
      onChange({ 
        industry: customIndustry.trim() as Industry,
        otherIndustryDetails: customIndustry.trim()
      });
      setCustomIndustry('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {industries.map((industry) => (
          <button
            key={industry.id}
            onClick={() => onChange({ industry: industry.id })}
            className={`p-6 rounded-xl border-2 transition-all ${
              data.industry === industry.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
            }`}
          >
            <h3 className="text-lg font-semibold">{industry.label}</h3>
          </button>
        ))}
      </div>
      
      <div ref={customInputRef} className="mt-6">
        <p className="text-sm text-gray-600 mb-2">Don't see your industry? Add it here:</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customIndustry}
            onChange={(e) => setCustomIndustry(e.target.value)}
            placeholder="Enter your industry"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleCustomIndustry}
            disabled={!customIndustry.trim()}
            className="p-2 text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Default export
export default IndustryStep;